import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import BrandLogo from "./BrandLogo";
import FooterTurismoIntelligente from "./footer/FooterTurismoIntelligente";
import FooterSezionePartner from "./footer/FooterSezionePartner";
import { useLang } from "../LangContext";
import "../footer-legal-bar.css";
import "../footer-main-grid.css";

const LegalLink = ({ to, testid, children }) => (
  <Link
    to={to}
    data-testid={testid}
    className="text-ink2 hover:text-terracotta transition-colors normal-case tracking-normal"
  >
    {children}
  </Link>
);

const AREA_ISTITUZIONALE = [
  { to: "/governance", label: "Governance" },
  { to: "/centrale-comando", label: "Progetti del territorio" },
  { to: "/ecosistema-governance", label: "Rete dei borghi" },
  { to: "/stanza-sindaci", label: "Stanza dei Sindaci" },
  { to: "/storia-protocollo", label: "Storia del progetto" },
  { to: "/richiedi-accesso?kind=comune", label: "Gestione Comune" },
  { to: "/richiedi-accesso?kind=proloco", label: "Pannello Pro Loco" },
  { to: "/pro-loco", label: "Pro Loco d'Italia" },
  { to: "/qr", label: "Guide QR" },
  { to: "/ufficio-stampa", label: "Ufficio stampa" },
  { to: "/registro-innovazione", label: "Storico dal 2013" },
];

export default function Footer() {
  const { t, lang } = useLang();
  const [areaOpen, setAreaOpen] = useState(false);

  useEffect(() => {
    if (!areaOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setAreaOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [areaOpen]);

  return (
    <footer className="site-footer border-t border-borderline bg-parchment2/40 paper-texture" data-testid="site-footer" lang={lang}>
      <div className="footer-main max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="footer-main__grid" data-testid="footer-main-grid">
          {/* Col 1 — Brand */}
          <div className="footer-main__col footer-main__col--brand">
            <div className="footer-main__logo mb-3">
              <BrandLogo />
            </div>
            <p className="footer-main__blurb text-ink2" data-testid="footer-site-description">
              {t("footer.blurb")}
            </p>
            <div
              data-testid="footer-trust-badge"
              className="footer-main__badge inline-flex items-center gap-2 text-[11px] font-mono tracking-[0.2em] uppercase text-ink2 border border-borderline bg-white/60 px-3 py-1.5 rounded-full"
              title={t("footer.trust")}
            >
              <span aria-hidden>🛡️</span>
              <span>{t("footer.trust")}</span>
            </div>
          </div>

          {/* Col 2 — Navigazione */}
          <nav className="footer-main__col" aria-labelledby="footer-nav-heading">
            <h5 id="footer-nav-heading" className="footer-main__heading">
              {t("footer.nav_title")}
            </h5>
            <ul className="footer-main__list">
              <li><Link to="/piazze">{t("nav.piazze")}</Link></li>
              <li><Link to="/eventi">{t("nav.events")}</Link></li>
              <li><Link to="/benvenuto">{t("footer.welcome_tour")}</Link></li>
              <li><Link to="/come-funziona">{t("footer.how_it_works")}</Link></li>
            </ul>
          </nav>

          {/* Col 3 — Community */}
          <nav className="footer-main__col" aria-labelledby="footer-community-heading">
            <h5 id="footer-community-heading" className="footer-main__heading">
              {t("footer.community_title")}
            </h5>
            <ul className="footer-main__list">
              <li><Link to="/manifesto">{t("footer.manifesto")}</Link></li>
              <li><Link to="/generazione-6amico" className="font-medium">{t("footer.generation")}</Link></li>
              <li><Link to="/censisci-luogo">{t("footer.census")}</Link></li>
              <li><Link to="/richiedi-accesso">{t("footer.request_access")}</Link></li>
            </ul>
          </nav>

          {/* Col 4 — Scopri */}
          <nav className="footer-main__col" aria-labelledby="footer-discover-heading">
            <h5 id="footer-discover-heading" className="footer-main__heading">
              {t("footer.discover_title")}
            </h5>
            <ul className="footer-main__list">
              <li><Link to="/mappa-del-mondo">{t("footer.map")}</Link></li>
              <li><Link to="/gemellaggi">{t("nav.twinnings")}</Link></li>
              <li><Link to="/vetrina">{t("footer.vetrina")}</Link></li>
              <li><Link to="/comuni">{t("footer.comuni")}</Link></li>
            </ul>
          </nav>
        </div>
      </div>

      <div className="border-t border-borderline bg-parchment/50" data-testid="footer-area-istituzionale">
        <button
          type="button"
          onClick={() => setAreaOpen((v) => !v)}
          aria-expanded={areaOpen}
          aria-controls="footer-area-istituzionale-panel"
          className="w-full max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-4 text-left hover:bg-parchment2/40 transition-colors"
          data-testid="footer-area-istituzionale-toggle"
        >
          <div className="min-w-0">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-terracotta mb-1">
              {t("footer.institutional")}
            </p>
            <p className="font-display text-lg sm:text-xl text-ink leading-snug">
              {t("footer.institutional_lead")}
            </p>
          </div>
          <span
            className={`shrink-0 flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-ink2 ${
              areaOpen ? "text-terracotta" : ""
            }`}
          >
            {areaOpen ? t("footer.close") : t("footer.open")}
            <ChevronDown
              size={18}
              className={`transition-transform duration-200 ${areaOpen ? "rotate-180" : ""}`}
              aria-hidden
            />
          </span>
        </button>
        {areaOpen && (
          <div
            id="footer-area-istituzionale-panel"
            className="border-t border-borderline/80"
            data-testid="footer-area-istituzionale-panel"
          >
            <ul
              className="max-w-[1400px] mx-auto px-5 md:px-10 py-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 text-sm text-ink list-none m-0"
              data-testid="footer-tech-nav"
            >
              {AREA_ISTITUZIONALE.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="hover:text-terracotta">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <FooterTurismoIntelligente />
      <FooterSezionePartner />

      <div className="border-t border-borderline py-4 px-5 bg-parchment/60">
        <div className="footer-legal-bar-scroll max-w-[1400px] mx-auto">
          <p className="footer-legal-bar" data-testid="footer-legal-bar" aria-label="Copyright e link legali">
            <span>© 2013-26 6AMICO - COMMUNITY DEI BORGHI</span>
            <span className="footer-legal-bar__sep" aria-hidden>
              |
            </span>
            <LegalLink to="/privacy" testid="footer-privacy">
              Privacy &amp; GDPR
            </LegalLink>
            <span className="footer-legal-bar__sep" aria-hidden>
              |
            </span>
            <LegalLink to="/cookie-policy" testid="footer-cookie-policy">
              Cookie Policy
            </LegalLink>
            <span className="footer-legal-bar__sep" aria-hidden>
              |
            </span>
            <LegalLink to="/termini" testid="footer-termini">
              Termini d&apos;Uso
            </LegalLink>
            <span className="footer-legal-bar__sep" aria-hidden>
              |
            </span>
            <LegalLink to="/dati-societari" testid="footer-dati-societari">
              Dati Societari
            </LegalLink>
            <span className="footer-legal-bar__sep" aria-hidden>
              |
            </span>
            <span>Conforme GDPR - DSA - AI Act</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
